(() => {
  document.querySelectorAll('[data-fizo-order-rebind]').forEach((root) => {
    const ORDER_READY_RETRY_DELAYS = [0, 500, 800, 1200, 1800, 2500, 3500, 5000, 7000];
    let token = new URLSearchParams(location.search).get('token');
    const fromEmail = new URLSearchParams(location.search).get('via') === 'email';
    const status = root.querySelector('[data-fizo-order-status]');
    const loadingState = root.querySelector('[data-fizo-order-loading]');
    const loadingTitle = root.querySelector('[data-fizo-order-loading-title]');
    const loadingDetail = root.querySelector('[data-fizo-order-loading-detail]');
    const retryButton = root.querySelector('[data-fizo-order-retry]');
    const list = root.querySelector('[data-fizo-order-list]');
    const confirmation = root.querySelector('[data-fizo-order-confirmation]');
    const actions = root.querySelector('[data-fizo-order-actions]');
    const reviewButton = root.querySelector('[data-fizo-order-review]');
    const submit = root.querySelector('[data-fizo-order-submit]');
    const drawer = document.querySelector('[data-fizo-fit-profile][data-context="order-rebind"]');
    const endpoint = drawer?.dataset.rebindEndpoint || '/apps/fizo-order-rebind?resource=rebind';
    const bindings = new Map();
    const initialBindings = new Map();
    let order = null;
    let pendingLineIds = [];
    let undoState = null;

    const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    const setLoadingState = (title, detail, error = false, canRetry = false) => {
      loadingState.hidden = false;
      loadingState.classList.toggle('is-error', error);
      loadingTitle.textContent = title;
      loadingDetail.textContent = detail;
      retryButton.hidden = !canRetry;
    };
    const setResolvedToken = (nextToken) => {
      if (!nextToken || nextToken === token) return;
      token = nextToken;
      const url = new URL(location.href);
      url.searchParams.set('token', token);
      history.replaceState(history.state, '', url);
      if (drawer) drawer.dataset.resolvedToken = token;
      document.dispatchEvent(new CustomEvent('fizo:order-token-resolved', {detail: {token}}));
    };
    const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'}[character]));
    const endpointWithToken = (value) => {
      const url = new URL(value, location.origin);
      url.searchParams.set('token', token || '');
      return `${url.pathname}${url.search}`;
    };
    const profileId = (profile) => profile?.id || profile?.profileId || profile?.profile_id || '';
    const profileName = (profile) => profile?.displayName || profile?.display_name || profile?.name || 'Fit Profile';
    const withoutSize = (value) => String(value || '')
      .replace(/\s*\/\s*(?:US(?:\s*[MW])?|UK|EU(?:R)?)\s*\d+(?:\.\d+)?\s*$/i, '')
      .trim();
    const lineName = (line) => withoutSize(line?.name) || 'Order item';
    const lineMeta = (line) => [withoutSize(line?.variantTitle), line?.sku, `Qty ${line?.unfulfilledQuantity ?? 0}`]
      .filter(Boolean)
      .map(escapeHtml)
      .join(' · ');
    const targetSize = (profile) => profile?.effectiveSizeUsM ?? profile?.effectiveRightSizeUsM ?? profile?.effective_right_size_us_m ?? profile?.manualSizeUsM ?? profile?.manual_size_us_m ?? '';
    const selectedInputs = () => [...list.querySelectorAll('[data-line-item]:checked')];
    const selectedIds = () => selectedInputs().map((input) => input.value);
    const lineById = (id) => order?.lineItems?.find((line) => String(line.id) === String(id));
    const changedLineIds = () => [...new Set([...initialBindings.keys(), ...bindings.keys()])].filter((id) => {
      const initial = initialBindings.get(id);
      const current = bindings.get(id);
      return String(initial?.id || '') !== String(current?.id || '');
    });

    const setSelection = (ids) => {
      const selected = new Set(ids.map(String));
      list.querySelectorAll('[data-line-item]').forEach((input) => { input.checked = selected.has(input.value); });
      syncSelection();
    };
    const syncSelection = () => {
      const inputs = [...list.querySelectorAll('[data-line-item]')];
      const checked = inputs.filter((input) => input.checked);
      const selectAll = list.querySelector('[data-select-all]');
      if (selectAll) {
        selectAll.checked = inputs.length > 0 && checked.length === inputs.length;
        selectAll.indeterminate = checked.length > 0 && checked.length < inputs.length;
      }
      const bar = list.querySelector('[data-fizo-batch-bar]');
      if (bar) bar.hidden = checked.length === 0;
      const count = bar?.querySelector('[data-fizo-selected-count]');
      if (count) count.textContent = `${checked.length} item${checked.length === 1 ? '' : 's'} selected`;
      const batchRemove = bar?.querySelector('[data-fizo-batch-remove]');
      if (batchRemove) batchRemove.disabled = checked.some((input) => initialBindings.has(String(input.value)));
      const batchAssign = bar?.querySelector('[data-fizo-batch-assign]');
      if (batchAssign) batchAssign.disabled = checked.some((input) => initialBindings.has(String(input.value)));
      updateSummary();
    };
    const updateSummary = (message = '') => {
      if (message) { status.textContent = message; return; }
      const selected = selectedInputs().length;
      const assigned = bindings.size;
      const changed = changedLineIds().length;
      status.textContent = `${assigned} item${assigned === 1 ? '' : 's'} assigned${selected ? ` · ${selected} selected` : ''}${changed ? ` · ${changed} unsaved change${changed === 1 ? '' : 's'}` : ''}.`;
      reviewButton.disabled = changed === 0;
      submit.disabled = changed === 0;
    };
    const renderRow = (lineId) => {
      const row = list.querySelector(`[data-order-line][data-line-id="${CSS.escape(String(lineId))}"]`);
      if (!row) return;
      const locked = initialBindings.has(String(lineId));
      const changeButton = row.querySelector('[data-fizo-line-change]');
      if (changeButton) changeButton.hidden = locked;
      const removeButton = row.querySelector('[data-fizo-line-remove]');
      if (removeButton) removeButton.hidden = locked;
      const binding = bindings.get(String(lineId));
      row.classList.toggle('is-bound', Boolean(binding));
      const marker = row.querySelector('[data-bound-profile]');
      if (marker) marker.hidden = !binding;
      if (!binding) return;
      row.querySelector('[data-bound-profile-name]').textContent = binding.name;
    };
    const renderAllRows = () => order?.lineItems?.forEach((line) => renderRow(String(line.id)));
    const snapshot = (ids) => new Map(ids.map((id) => [String(id), bindings.get(String(id)) || null]));
    const showUndo = (message, before) => {
      undoState = before;
      let toast = root.querySelector('[data-fizo-order-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'fizo-order-rebind__toast';
        toast.dataset.fizoOrderToast = '';
        toast.innerHTML = '<span data-fizo-toast-message></span><button type="button" data-fizo-order-undo>Undo</button>';
        actions.before(toast);
      }
      toast.querySelector('[data-fizo-toast-message]').textContent = message;
      toast.hidden = false;
    };
    const restoreSnapshot = () => {
      if (!undoState) return;
      undoState.forEach((binding, id) => binding ? bindings.set(id, binding) : bindings.delete(id));
      renderAllRows();
      updateSummary('Previous profile assignment restored.');
      root.querySelector('[data-fizo-order-toast]')?.setAttribute('hidden', '');
      undoState = null;
    };
    const removeBindings = (ids) => {
      if (ids.some((id) => initialBindings.has(String(id)))) {
        updateSummary('A confirmed fit profile cannot be removed.');
        return;
      }
      const boundIds = ids.filter((id) => bindings.has(String(id)));
      if (!boundIds.length) { updateSummary('The selected items do not have a fit profile.'); return; }
      const before = snapshot(boundIds);
      boundIds.forEach((id) => bindings.delete(String(id)));
      renderAllRows();
      setSelection([]);
      confirmation.hidden = true;
      showUndo(`${boundIds.length} profile assignment${boundIds.length === 1 ? '' : 's'} removed.`, before);
    };
    const openProfilePicker = (ids) => {
      if (!ids.length) { updateSummary('Select at least one order item before choosing a fit profile.'); return; }
      if (ids.some((id) => initialBindings.has(String(id)))) {
        updateSummary('A confirmed fit profile cannot be changed.');
        return;
      }
      pendingLineIds = ids.map(String);
      const trigger = root.querySelector('[data-fizo-profile-trigger]');
      trigger?.click();
    };
    const renderReview = () => {
      const rows = changedLineIds().map((lineId) => {
        const binding = bindings.get(lineId);
        const line = lineById(lineId);
        if (!binding) return `<div class="fizo-order-rebind__review-row"><div><strong>${escapeHtml(lineName(line))}</strong><small>Remove fit profile</small></div></div>`;
        return `<div class="fizo-order-rebind__review-row"><div><strong>${escapeHtml(lineName(line))}</strong><small>Assign fit profile</small></div><span>${escapeHtml(binding.name)}</span></div>`;
      }).join('');
      confirmation.innerHTML = `<header><strong>Review changes</strong><button type="button" data-fizo-review-close aria-label="Close review">×</button></header>${rows}`;
      confirmation.hidden = false;
      confirmation.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    };

    const renderOrder = (payload) => {
        const orders = payload.orders || [];
        if (!orders.length) throw new Error('There are no eligible orders to update.');
        setResolvedToken(payload.token);
        order = orders[0];
        order.lineItems.forEach((line) => {
          if (!line.fitProfile || !profileId(line.fitProfile)) return;
          bindings.set(String(line.id), {
            id: profileId(line.fitProfile),
            name: profileName(line.fitProfile),
            targetSize: targetSize(line.fitProfile),
            profile: line.fitProfile,
          });
          initialBindings.set(String(line.id), bindings.get(String(line.id)));
        });
        list.innerHTML = `<article class="fizo-order-rebind__order"><header><div><h2>${escapeHtml(order.name || 'Order')}</h2><p>${escapeHtml(new Date(order.createdAt).toLocaleDateString())} · ${escapeHtml(order.fulfillmentStatus || 'Before shipping')}</p></div><label class="fizo-order-rebind__select-all"><input type="checkbox" data-select-all> Select all</label></header><div class="fizo-order-rebind__batch" data-fizo-batch-bar hidden><strong data-fizo-selected-count>0 items selected</strong><div><button type="button" data-fizo-batch-assign>Assign profile</button><button type="button" data-fizo-batch-remove>Remove profile</button></div></div><div class="fizo-order-rebind__lines">${order.lineItems.map((line) => `<div class="fizo-order-rebind__line" data-order-line data-line-id="${escapeHtml(line.id)}"><input type="checkbox" value="${escapeHtml(line.id)}" data-line-item aria-label="Select ${escapeHtml(lineName(line))}"><div class="fizo-order-rebind__line-details"><strong>${escapeHtml(lineName(line))}</strong><small>${lineMeta(line)}</small></div><div class="fizo-order-rebind__profile" data-bound-profile hidden><small>Fit profile</small><strong data-bound-profile-name></strong><div class="fizo-order-rebind__row-actions"><button type="button" data-fizo-line-change>Change</button><button type="button" data-fizo-line-remove>Remove profile</button></div></div></div>`).join('')}</div></article>`;
        const selectAll = list.querySelector('[data-select-all]');
        selectAll?.addEventListener('change', () => setSelection(selectAll.checked ? order.lineItems.map((line) => String(line.id)) : []));
        list.addEventListener('change', (event) => { if (event.target.matches('[data-line-item]')) syncSelection(); });
        actions.hidden = false;
        loadingState.hidden = true;
        renderAllRows();
        updateSummary();
    };
    const loadOrder = async () => {
      retryButton.disabled = true;
      status.textContent = '';
      setLoadingState('Preparing your fit options…', 'Your order is confirmed. This usually takes only a few seconds.');
      let lastError = null;
      for (const retryDelay of ORDER_READY_RETRY_DELAYS) {
        if (retryDelay) await delay(retryDelay);
        try {
          const response = await fetch(endpointWithToken('/apps/fizo-order-rebind'), {credentials: 'include', headers: {Accept: 'application/json'}});
          const payload = await response.json().catch(() => ({}));
          const error = Object.assign(new Error(payload.error || 'Eligible orders could not be loaded.'), {status: response.status});
          if (response.status === 202) { lastError = error; continue; }
          if (response.ok) {
            renderOrder(payload);
            retryButton.disabled = false;
            if (fromEmail) {
              fetch(endpointWithToken('/apps/fizo-order-rebind?resource=email-visit&via=email'), {
                method: 'POST', credentials: 'include', headers: {Accept: 'application/json'},
              }).then((visitResponse) => {
                if (!visitResponse.ok) console.warn('Fit email visit audit could not be recorded');
              }).catch(() => console.warn('Fit email visit audit could not be recorded'));
            }
            return;
          }
          if (response.status < 500) throw error;
          lastError = error;
        } catch (error) {
          lastError = error;
          if (error.status && error.status !== 202 && error.status < 500) break;
        }
      }
      retryButton.disabled = false;
      const terminalError = lastError?.status >= 400 && lastError.status < 500;
      setLoadingState(
        terminalError ? 'This fit link cannot be opened' : 'This is taking longer than expected',
        terminalError ? (lastError?.message || 'Please check that you are signed in with the account used for this order.') : 'Your payment is safe. Shopify is still preparing the order data; please try again in a moment.',
        true,
        !terminalError,
      );
    };

    if (!endpoint) {
      setLoadingState('This fit link is unavailable', 'Please contact support for help with this order.', true, false);
      return;
    }
    retryButton?.addEventListener('click', loadOrder);
    loadOrder();

    list.addEventListener('click', (event) => {
      if (event.target.closest('[data-fizo-batch-assign]')) { openProfilePicker(selectedIds()); return; }
      if (event.target.closest('[data-fizo-batch-remove]')) { removeBindings(selectedIds()); return; }
      const row = event.target.closest('[data-order-line]');
      if (!row) return;
      const id = row.dataset.lineId;
      if (event.target.closest('[data-fizo-line-change]')) { openProfilePicker([id]); return; }
      if (event.target.closest('[data-fizo-line-remove]')) { removeBindings([id]); return; }
    });
    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-fizo-order-undo]')) restoreSnapshot();
      if (event.target.closest('[data-fizo-review-close]')) confirmation.hidden = true;
    });
    reviewButton?.addEventListener('click', renderReview);

    document.addEventListener('fizo:order-profile-confirmed', (event) => {
      const profile = event.detail?.profile || null;
      const ids = pendingLineIds.length ? pendingLineIds : selectedIds();
      if (!profile || !ids.length || !profileId(profile)) { updateSummary('Select at least one order item and a fit profile.'); return; }
      if (ids.some((id) => initialBindings.has(String(id)))) { updateSummary('A confirmed fit profile cannot be changed.'); return; }
      const before = snapshot(ids);
      ids.forEach((id) => bindings.set(String(id), {id: profileId(profile), name: profileName(profile), targetSize: targetSize(profile), profile}));
      renderAllRows();
      setSelection([]);
      pendingLineIds = [];
      confirmation.hidden = true;
      showUndo(`${ids.length} item${ids.length === 1 ? '' : 's'} assigned to ${profileName(profile)}.`, before);
    });

    submit?.addEventListener('click', async () => {
      const changed = changedLineIds();
      const assignments = changed.flatMap((lineItemId) => {
        const profile = bindings.get(lineItemId);
        return profile ? [{lineItemId, profileId: profile.id}] : [];
      });
      if (!changed.length) { updateSummary('There are no fit profile changes to save.'); return; }
      if (!assignments.length) { updateSummary('Only unsaved profile selections can be removed.'); return; }
      submit.disabled = true;
      status.textContent = 'Updating your order…';
      try {
        const response = await fetch(endpointWithToken(endpoint), {method: 'POST', credentials: 'include', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({assignments})});
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Your order could not be updated.');
        status.textContent = `${payload.order?.name || order?.name || 'Your order'} has been updated successfully.`;
        list.querySelectorAll('input, button').forEach((control) => { control.disabled = true; });
        confirmation.hidden = true;
        actions.hidden = true;
        root.querySelector('[data-fizo-order-toast]')?.setAttribute('hidden', '');
      } catch (error) { status.textContent = error.message; submit.disabled = false; }
    });
  });
})();
